if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.addonContainer = this.querySelector('[data-loot-product-addons]');
        if (this.addonContainer) {
          this.addonContainer.addEventListener('change', (event) => {
            if (!event.target.matches('[data-loot-addon-input]')) return;
            this.updateAddonSummary();
          });
          this.updateAddonSummary();

          this.addonPlaceholder = document.createComment('loot-product-addons-home');
          this.addonContainer.parentNode?.insertBefore(this.addonPlaceholder, this.addonContainer);
          this.syncAddonLayout();
        }
      }

      syncAddonLayout() {
        if (!this.addonContainer || !this.addonPlaceholder) return;

        const infoContainer = this.closest('.product__info-container');
        const quantityBlock = infoContainer?.querySelector(':scope > .product-form__quantity');

        // Ürün sayfasında masaüstü ve mobilde eklenti alanını
        // “Kaç tane olsun?” bloğunun hemen üstüne yerleştir.
        if (infoContainer && quantityBlock) {
          if (
            this.addonContainer.parentNode !== infoContainer ||
            this.addonContainer.nextElementSibling !== quantityBlock
          ) {
            infoContainer.insertBefore(this.addonContainer, quantityBlock);
          }
          return;
        }

        // Hızlı ürün görünümü gibi adet bloğu olmayan yerlerde özgün konumu koru.
        const originalParent = this.addonPlaceholder.parentNode;
        if (originalParent && this.addonContainer.parentNode !== originalParent) {
          originalParent.insertBefore(this.addonContainer, this.addonPlaceholder.nextSibling);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const selectedAddons = this.getSelectedAddons();
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        const addRequest = selectedAddons.length
          ? this.addProductWithAddons(formData, selectedAddons)
          : (() => {
              config.body = formData;
              return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
            })();

        addRequest
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getSelectedAddons() {
        if (!this.addonContainer) return [];
        return Array.from(this.addonContainer.querySelectorAll('[data-loot-addon-input]:checked'))
          .map((input) => ({
            id: Number(input.dataset.variantId),
            price: Number(input.dataset.price || 0),
            title: input.dataset.addonTitle || '',
          }))
          .filter((addon) => Number.isFinite(addon.id) && addon.id > 0);
      }

      updateAddonSummary() {
        if (!this.addonContainer) return;
        const selected = this.getSelectedAddons();
        this.addonContainer.querySelectorAll('[data-loot-addon-row]').forEach((row) => {
          row.classList.toggle('is-selected', Boolean(row.querySelector('[data-loot-addon-input]:checked')));
        });

        const totalElement = this.addonContainer.querySelector('[data-loot-addon-total]');
        if (!totalElement) return;
        const total = selected.reduce((sum, addon) => sum + addon.price, 0);
        totalElement.hidden = total <= 0;
        if (total > 0) totalElement.textContent = `Seçilenler +${this.formatAddonMoney(total)}`;
      }

      formatAddonMoney(cents) {
        const currency = this.addonContainer?.dataset.currency || window.Shopify?.currency?.active || 'TRY';
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {
            style: 'currency',
            currency,
            minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
          }).format(cents / 100);
        } catch (error) {
          return `${(cents / 100).toFixed(2)} ${currency}`;
        }
      }

      collectLineItemProperties(formData) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (!match || value === '' || value == null) continue;
          properties[match[1]] = value;
        }
        return properties;
      }

      async addProductWithAddons(formData, selectedAddons) {
        const variantId = Number(formData.get('id'));
        const quantity = Math.max(1, parseInt(formData.get('quantity'), 10) || 1);
        const matchQuantity = this.addonContainer?.dataset.matchQuantity !== 'false';
        const addonQuantity = matchQuantity ? quantity : 1;
        const productTitle = this.addonContainer?.dataset.productTitle || document.title;
        const groupId = `EKL-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
        const properties = this.collectLineItemProperties(formData);
        properties['_Loot Eklenti Grubu'] = groupId;

        const mainItem = { id: variantId, quantity, properties };
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

        const items = [
          mainItem,
          ...selectedAddons.map((addon) => ({
            id: addon.id,
            quantity: addonQuantity,
            properties: {
              'Ana ürün': productTitle,
              '_Loot Eklenti Grubu': groupId,
              '_Ürün Eklentisi': 'true',
            },
          })),
        ];

        const root = window.Shopify?.routes?.root || '/';
        const addUrl = window.routes?.cart_add_url || `${root}cart/add.js`;
        const updateUrl = window.routes?.cart_update_url || `${root}cart/update.js`;
        const sections = this.cart?.getSectionsToRender?.().map((section) => section.id) || [];

        const addResponse = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        });
        const addData = await addResponse.json();
        if (!addResponse.ok || addData?.status) return addData;
        if (!this.cart) return addData;

        const updateResponse = await fetch(updateUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            sections,
            sections_url: window.location.pathname,
          }),
        });
        return updateResponse.json();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.submitButton.dataset.lootAddLabel || window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

/* Lootsepeti v61: card-wide links must never steal clicks from add-to-cart controls. */
document.addEventListener('click', (event) => {
  const addControl = event.target.closest(
    '.product-card-wrapper .quick-add__submit, .product-card-wrapper .loot-card-link__button, .product-card-wrapper .loot-card-add button'
  );
  if (!addControl) return;
  event.stopPropagation();
});
